import type { FileWithLatestFileVersion } from "schemas/generated/file";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from "@replo/design-system/components/menu/Menu";
import { toast } from "@replo/design-system/components/toast/Toast";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import {
  nextJSProjectRouter,
  nextJSProjectUtils,
} from "@v2/trpc/routes/nextJSProject";
import { Copy, Ellipsis, ExternalLink, Pencil, Trash } from "lucide-react";

export default function PageCardMenu({
  page,
  isPublished,
  publishedSiteUrl,
  setIsRenamingPage,
  onDeleteClick,
}: {
  page: FileWithLatestFileVersion;
  isPublished: boolean;
  publishedSiteUrl?: string;
  setIsRenamingPage: (isRenamingPage: boolean) => void;
  onDeleteClick: () => void;
}) {
  const projectId = useCurrentProjectId();

  const { mutate: duplicatePage, isPending: isDuplicating } =
    nextJSProjectRouter.duplicatePage.useMutation({
      onSuccess: async ({ createdFile }) => {
        void nextJSProjectUtils.listPages.invalidate({ projectId });
        toast.success({
          title: "Page duplicated",
          description: `"${createdFile.name}" was created`,
        });
      },
    });

  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            icon
            variant="secondary"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
          >
            <Ellipsis />
          </Button>
        }
      />
      <MenuContent align="start" side="bottom">
        <MenuItem
          label="Rename"
          startEnhancer={<Pencil />}
          onClick={() => {
            setIsRenamingPage(true);
          }}
        />
        <MenuItem
          label="Duplicate"
          startEnhancer={<Copy />}
          disabled={isDuplicating}
          onClick={() => {
            if (projectId) {
              duplicatePage({
                projectId,
                fileId: page.id,
              });
            }
          }}
        />
        {isPublished && publishedSiteUrl && (
          <MenuItem
            label="View live"
            startEnhancer={<ExternalLink />}
            onClick={() => {
              window.open(publishedSiteUrl, "_blank");
            }}
          />
        )}
        <MenuSeparator />
        <MenuItem
          label="Delete"
          variant="destructive"
          startEnhancer={<Trash />}
          onClick={onDeleteClick}
        />
      </MenuContent>
    </Menu>
  );
}
